import ReactDOM from 'react-dom';
import { transform2PureRoute, splitRoutes, getParentRoute, isSameDomain, loadPage } from '../utils';

/**
 * 缓存页面的渲染方法
 */
export const cacheSubPages = new Map<string, (instanceId: string) => void>();
/**
 * 缓存页面的渲染实例
 */
export const cachePageInstances = new Map<string, any>();

// 缓存子页面的设置方法
export const subPageSetterMapping = new Map<string, (reactNode: React.ReactNode) => void>();

declare let MAX_ROUTES: Record<string, string>;
declare let GET_TOP_SCOPE: () => any;

/**
 * 页面渲染管理类
 *
 * @description
 * MaxPageRender 是路由系统的核心组件之一，负责在路由变化时协调页面组件的加载、渲染和卸载过程。
 * 它实现了异步的页面生命周期管理，确保页面在正确的时机完成挂载和卸载，并维护必要的状态缓存。
 * 支持嵌套路由的处理，能够准确识别和处理路由变更中的页面差异。
 */
export class MaxPageRender {
  constructor() {}

  // 上一次路由
  public lastRoute = '';

  // 记录卸载完毕的回调函数
  private _unMountResolveMap: Map<string, (value: unknown) => void> = new Map();
  // 记录挂载完毕的回调函数
  public mountResolveMap: Map<string, (value: unknown) => void> = new Map();

  // 供页面React根节点生命周期调用，标识当前页面已经卸载完成
  public pageUnMountFinish(pageCode: string) {
    // 执行回调 resolve，标记卸载完成态
    this._unMountResolveMap.get(pageCode)?.(true);
    this._unMountResolveMap.delete(pageCode);
  }

  // 供页面React根节点生命周期调用，标识当前页面已经挂载完成
  public pageMountFinish(route: string) {
    // 执行回调 resolve，标记卸载完成态
    this.mountResolveMap.get(route)?.(true);
    this.mountResolveMap.delete(route);
  }

  // 卸载单个页面
  private async _unMountPage(url: string, needActiveUnmount: boolean) {
    const pureUrl = transform2PureRoute(url);
    // 卸载页面
    const pageCode = MAX_ROUTES[pureUrl];
    // 获取父路由
    const parentRoute = getParentRoute(pureUrl);

    // 没有父路由，说明页面在根dom下
    if (!parentRoute && !needActiveUnmount) {
      // 直接返回完成态，无需主动卸载，根节点挂载新页面时react会自动卸载原有页面
      return Promise.resolve(true);
    }

    // 返回异步处理
    return new Promise((resolve, reject) => {
      // 设置 resolve 一定要在卸载页面之前就配置，否则会出现页面先卸载后拿不到 resolve 的情况
      this._unMountResolveMap.set(pageCode, resolve);

      // 卸载父路由下的子路由页面
      const parentPageCode = MAX_ROUTES[parentRoute];
      if (parentPageCode) {
        // 卸载子路由页面
        subPageSetterMapping.get(parentPageCode)?.(null);

        const pageInstance = cachePageInstances.get(pageCode);
        // 子页面没开启缓存配置则清除掉缓存实例
        if (!pageInstance?.props?.isCache) {
          cachePageInstances.delete(pageCode);
        }
      }
      // 没有父路由，说明页面在根dom下，如果需要主动卸载，才卸载根dom下的页面，一般出现在clearAllStack并且不打开新页面的情况下
      // 如果普通的既要卸载旧页面也要挂载新页面时，不执行主动卸载，因为会导致新页面挂载之前的白屏问题
      else if (needActiveUnmount) {
        ReactDOM.unmountComponentAtNode(document.querySelector('#max-app'));
      }

      const top = GET_TOP_SCOPE();
      try {
        top.del(pageCode);
      } catch (e) {
        console.error(e);
      }
    });
  }

  // 挂载单个页面
  public async mountPage(url: string) {
    const pureUrl = transform2PureRoute(url);
    // 挂载页面
    const pageCode = MAX_ROUTES[pureUrl];

    // 返回异步处理
    return new Promise((resolve, reject) => {
      // 设置 resolve 一定要在挂载页面之前就配置，否则会出现页面先挂载后拿不到 resolve 的情况
      this.mountResolveMap.set(pageCode, resolve);
      // 挂载页面
      if (cacheSubPages.has(pageCode)) {
        const rerender = cacheSubPages.get(pageCode);
        rerender(pageCode);
      } else {
        loadPage(pageCode);
      }
    });
  }

  /**
   * 卸载多个页面
   * @param removeRoutes 待卸载的路由列表
   * @param needActiveUnmount 是否需要主动卸载，默认值为 true
   */
  public async unMount(removeRoutes: string[], needActiveUnmount: boolean) {
    // 逐个卸载页面
    // 页面卸载是异步的，等待所有页面卸载完成
    for (let i = 0; i < removeRoutes.length; i++) {
      const removeRoute = removeRoutes[i];
      // 等待当前路由对应的页面卸载完再继续卸载下一个
      await this._unMountPage(removeRoute, needActiveUnmount);
    }
  }
  public async mount(createRoutes: string[]) {
    // 逐个挂载页面
    // 页面挂载是异步的，等待所有页面挂载完成
    for (let i = 0; i < createRoutes.length; i++) {
      const createRoute = createRoutes[i];
      // 等待当前路由对应的页面挂载完再继续挂载下一个
      await this.mountPage(createRoute);
    }
  }

  public async render(url: string, isForceReplace?: boolean) {
    const targetRoute = transform2PureRoute(url);
    // 根页面路由不一致时，清除子页面缓存，确保下次再跳转回当前根页面路由下的子页面时内容要重新初始化。
    if (!isSameDomain(this.lastRoute, targetRoute)) {
      cachePageInstances.clear();
    }
    // 切分父子路由并diff路由
    const { created, removed } = splitRoutes(targetRoute, this.lastRoute, isForceReplace);
    // 自底向上卸载路由
    await this.unMount([...removed].reverse(), created.length === 0);
    // 自顶向下挂载路由
    await this.mount(created);

    // 记录上一次路由
    this.lastRoute = targetRoute;
  }

  public clear() {
    this.lastRoute = '';
  }
}
